/**
 * AiSuggestButton — small coral action that fills a metadata field with an AI
 * suggestion from the postgen per-field suggest endpoint.
 *
 * - Sparkle icon + the word "Suggest" (NOT "AI" — that word already means
 *   provenance on this screen).
 * - Click → spinner → calls the endpoint → fills the input. The reviewer sees
 *   it land in the field and the band meter reacts live.
 * - **Undo:** the rail auto-saves any field change (a suggestion is
 *   indistinguishable from typing), so the button stashes the value it replaced
 *   and offers an inline Undo. A suggestion the reviewer dislikes is always one
 *   click from being put back — nothing is ever lost to a bad suggestion.
 * - Failure → quiet toast, field untouched. Never blocks.
 * - Disabled while a suggestion is in flight and while the whole-run generation
 *   is running (don't race MetadataGenerationControl).
 *
 * An accepted suggestion saves through the existing patchContent path and reads
 * as "Your edit" in the provenance chip system — which is the truth.
 *
 * ## Preview mode (`preview?: boolean`)
 * When `preview` is true the button does NOT immediately fill the field.
 * Instead, on endpoint success it stashes the suggestion in local state and
 * renders a current→suggested comparison inline. The reviewer chooses:
 *   - Accept → onSuggest(pending) applies the value (the caller persists it).
 *   - Reject → clears the preview, the field is never touched.
 * This is the PREVIEW→ACCEPT flow used by the authored-schema section
 * (article_description + Force-HowTo). The default (`preview=false`) preserves
 * the existing fill+undo behavior so MetadataFixRow and other current callers
 * keep working unchanged.
 */

import { useState, useRef, useEffect } from 'react';
import { Sparkles, Loader2, Undo2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { blogCreationService } from '@/services/blogCreationService';

export interface AiSuggestButtonProps {
  /** Metadata field name (snake_case, matching the backend allow-list). */
  field: string;
  /** Callback to fill the input with the suggested value. */
  onSuggest: (value: string) => void;
  /** Content ID for the API call. */
  contentId: string;
  /** Tenant ID for the API call. */
  tenantId: string;
  /** The value currently in the field — stashed so a suggestion can be undone. */
  currentValue?: string;
  /** Disable while whole-run generation is in flight. */
  disabled?: boolean;
  /**
   * Preview mode: when true, on endpoint success the suggestion is held in
   * local state and the reviewer must Accept (→ onSuggest) or Reject (clear).
   * Default false preserves the existing fill + inline-Undo behavior.
   */
  preview?: boolean;
  /**
   * Optional per-field request context forwarded to the suggest endpoint's
   * `params` body field as-is (e.g. `{ image_src: row.src }` for
   * field="image_alt" — WS-3 phase 2, vision). Omitted for every other field.
   */
  extraParams?: Record<string, unknown>;
  /**
   * S5 (2026-07-22) — ADDITIVE presentation variant. `'default'` preserves
   * the existing coral styling pixel-for-pixel at every current call site
   * (SocialMetadataCard, SerpPreviewCard, SummaryCard, ImageMetadataCard —
   * untouched). `'quiet'` is the Krug banner-blindness treatment for the
   * structured-data drawer sockets: an inline text action, muted →
   * foreground on hover, no pill/border emphasis.
   */
  variant?: 'default' | 'quiet';
  /**
   * S5 — ADDITIVE verb-first label override (e.g. "Suggest a summary",
   * "Suggest steps"). Defaults to the original "Suggest".
   */
  label?: string;
  /**
   * Render the trigger as a filled, high-emphasis coral button (used where the
   * suggestion is a primary affordance, e.g. next to the article title).
   * Default is the minimal inline text style used inside rail cards.
   */
  prominent?: boolean;
}

const CONTROL_CLASSES =
  'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs font-semibold ' +
  'bg-transparent border-0 cursor-pointer motion-safe:transition-colors ' +
  'focus:outline-none focus-visible:ring-1 focus-visible:ring-coral';

export function AiSuggestButton({
  field,
  onSuggest,
  contentId,
  tenantId,
  currentValue,
  disabled = false,
  preview = false,
  extraParams,
  variant = 'default',
  label = 'Suggest',
  prominent = false,
}: AiSuggestButtonProps) {
  const [loading, setLoading] = useState(false);
  const [replaced, setReplaced] = useState<string | null>(null);
  // Preview-mode only: the held suggestion awaiting Accept/Reject. Null when
  // no preview is active. In default mode this stays null and the value
  // applies immediately via onSuggest (with Undo for one-click revert).
  const [pending, setPending] = useState<string | null>(null);

  // S5: transient reject feedback (preview mode only — default mode is
  // untouched). ~2.5s, aria-live polite, muted. Accept remains the ONLY
  // persist path: a suggestion is never applied without an explicit Accept.
  const [rejectNote, setRejectNote] = useState(false);
  const rejectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (rejectTimerRef.current) clearTimeout(rejectTimerRef.current);
    };
  }, []);

  const handleClick = async () => {
    if (loading || disabled || pending !== null) return;
    setLoading(true);
    try {
      const value = extraParams
        ? await blogCreationService.suggestField(contentId, field, tenantId, extraParams)
        : await blogCreationService.suggestField(contentId, field, tenantId);
      if (value && value.trim()) {
        if (preview) {
          // Hold for review — do NOT touch the field yet.
          setPending(value);
        } else {
          setReplaced(currentValue ?? '');
          onSuggest(value);
        }
      } else {
        toast.error("Couldn't get a suggestion — try again");
      }
    } catch {
      toast.error("Couldn't get a suggestion — try again");
    } finally {
      setLoading(false);
    }
  };

  const handleUndo = () => {
    if (replaced === null) return;
    onSuggest(replaced);
    setReplaced(null);
  };

  const handleAccept = () => {
    if (pending === null) return;
    onSuggest(pending);
    setPending(null);
  };

  const handleReject = () => {
    setPending(null);
    // S5: honest reject feedback — the field is untouched and nothing ships.
    setRejectNote(true);
    if (rejectTimerRef.current) clearTimeout(rejectTimerRef.current);
    rejectTimerRef.current = setTimeout(() => setRejectNote(false), 2500);
  };

  const isDisabled = loading || disabled || pending !== null;

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        title={
          disabled
            ? 'A whole-run metadata generation is in progress'
            : pending !== null
              ? 'Review the pending suggestion first'
              : 'Get an AI suggestion for this field'
        }
        className={cn(
          prominent
            ? 'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ' +
                'bg-coral text-white shadow-sm cursor-pointer motion-safe:transition-colors ' +
                'hover:bg-coral-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/50'
            : cn(
                CONTROL_CLASSES,
                // S5: quiet actions are deliberately muted inside structured-data sockets.
                variant === 'quiet'
                  ? 'text-muted-foreground hover:text-foreground'
                  : 'text-coral hover:text-coral-hover',
              ),
          // The staging beat marks the new high-emphasis title affordance.
          // S5 keeps default and quiet card actions pixel-stable.
          prominent && !isDisabled && 'animate-ai-suggest animate-ai-suggest-coral',
          isDisabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        {loading ? (
          <Loader2 className={cn('animate-spin', prominent ? 'size-3.5' : 'size-3')} />
        ) : (
          <Sparkles className={prominent ? 'size-3.5' : 'size-3'} />
        )}
        {label}
      </button>

      {/* Preview mode: current→suggested comparison + Accept/Reject */}
      {preview && pending !== null && (
        <span
          className="inline-flex flex-wrap items-center gap-1 rounded border border-coral/30 bg-coral-light/40 px-1.5 py-0.5"
          data-testid="suggest-preview"
        >
          {currentValue && currentValue.trim() && (
            <span className="text-[11px] text-muted-foreground line-through decoration-coral/40 max-w-[12rem] truncate">
              {currentValue}
            </span>
          )}
          <span className="text-coral" aria-hidden="true">
            →
          </span>
          <span className="text-[11px] text-foreground font-medium max-w-[16rem] truncate">
            {pending}
          </span>
          <button
            type="button"
            onClick={handleAccept}
            title="Apply the suggestion"
            className={cn(
              CONTROL_CLASSES,
              'text-teal-dark hover:text-teal',
            )}
          >
            <Check className="size-3" />
            Accept
          </button>
          <button
            type="button"
            onClick={handleReject}
            title="Discard the suggestion"
            className={cn(
              CONTROL_CLASSES,
              'text-muted-foreground hover:text-foreground',
            )}
          >
            <X className="size-3" />
            Reject
          </button>
        </span>
      )}

      {/* S5: transient reject feedback (preview mode only) */}
      {preview && rejectNote && (
        <span
          className="inline-flex items-center text-[10.5px] text-muted-foreground"
          data-testid="reject-note"
          aria-live="polite"
        >
          Nothing changed — not shipping.
        </span>
      )}

      {/* Default mode: inline Undo (kept off in preview mode — preview has Reject) */}
      {!preview && replaced !== null && !loading && (
        <button
          type="button"
          onClick={handleUndo}
          title="Put back the value the suggestion replaced"
          className={cn(CONTROL_CLASSES, 'text-muted-foreground hover:text-foreground')}
        >
          <Undo2 className="size-3" />
          Undo
        </button>
      )}
    </span>
  );
}
